import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, DollarSign, ArrowRight, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCurrency } from "@/hooks/use-currency";
import { getOptimizedImageUrl } from "@/lib/utils";
import { useLocalized } from "@/hooks/useLocalized";
import { LocalizedContent } from "@/types";
import { useTranslation } from "react-i18next";

interface Service {
  id: string;
  photographer_id: string;
  name: LocalizedContent;
  category: string;
  description: LocalizedContent;
  duration_minutes: number;
  base_price: number;
  media_urls: string[];
}

interface ServiceSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onServiceSelect: (service: Service) => void;
}

export function ServiceSelectionModal({ isOpen, onClose, onServiceSelect }: ServiceSelectionModalProps) {
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();
  const { getLocalized } = useLocalized();
  const [services, setServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}min`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}min`;
  };

  useEffect(() => {
    const fetchServices = async () => {
      if (!isOpen) return;

      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('services')
          .select('*')
          .eq('is_active', true)
          .order('name'); // Note: sorting by JSON name might not work as perfectly as text name, but acceptable for now

        if (error) throw error;
        setServices(data || []);
      } catch (error) {
        console.error('Error fetching services:', error);
        toast({
          title: "Error loading services",
          description: t('common.errorGeneric', "Please try again later."),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchServices();
  }, [isOpen, toast, t]);

  const handleServiceSelect = (service: Service) => {
    onServiceSelect(service);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{t('booking.selectServiceTitle', 'Select Photography Service')}</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="ml-2">{t('common.loading', 'Loading services...')}</span>
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">{t('common.noServices', 'No services available at the moment.')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service) => (
              <Card key={service.id} className="group hover:shadow-[var(--shadow-elegant)] transition-all duration-300 overflow-hidden cursor-pointer">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={getOptimizedImageUrl(service.media_urls[0] || "https://images.unsplash.com/photo-1606216794074-735e91aa2c92?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80", { width: 500, height: 300, resize: 'cover' })}
                    alt={getLocalized(service.name)}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute top-4 left-4">
                    <Badge variant="secondary" className="capitalize">
                      {service.category}
                    </Badge>
                  </div>
                </div>

                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">{getLocalized(service.name)}</h3>
                  <p className="text-muted-foreground mb-4 line-clamp-3">
                    {getLocalized(service.description)}
                  </p>

                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      {formatDuration(service.duration_minutes)}
                    </div>
                    <div className="flex items-center gap-2 font-semibold text-primary">
                      <DollarSign className="h-4 w-4" />
                      {formatPrice(service.base_price)}
                    </div>
                  </div>

                  <Button
                    className="w-full group/btn"
                    onClick={() => handleServiceSelect(service)}
                  >
                    {t('booking.selectThisService', 'Select This Service')}
                    <ArrowRight className="h-4 w-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="flex justify-center pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel', 'Cancel')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}